import { createSelector, createSlice } from '@reduxjs/toolkit';

const companySlice = createSlice({
    initialState: {
        data: [],
        isLoading: false,
        singleCompany: null,
        isUpdating: false,
        schedulePresets: [],
        companyLogoUrl: null,
    },
    name: 'companies',
    reducers: {
        fetchCompanies: state => {
            state.data = [];
            state.isLoading = true;
        },
        companiesFetched: (state, { payload }) => {
            const { results } = payload;
            state.data = results;
            state.isLoading = false;
        },
        companiesFetchedFailed: state => {
            state.isLoading = false;
        },

        // Single company (white-label mode)
        fetchSingleCompany: state => {
            state.isLoading = true;
        },
        singleCompanyFetched: (state, { payload }) => {
            state.singleCompany = payload;
            state.data = [payload];
            state.isLoading = false;
        },
        singleCompanyFetchFailed: state => {
            state.isLoading = false;
        },

        // Update company
        updateCompany: state => {
            state.isUpdating = true;
        },
        companyUpdated: (state, { payload }) => {
            state.singleCompany = payload;
            state.data = [payload];
            state.isUpdating = false;
        },
        companyUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Skill rates
        updateSkillRates: state => {
            state.isUpdating = true;
        },
        skillRatesUpdated: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.rates = payload.rates;
            }
            state.isUpdating = false;
        },
        skillRatesUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Excluded sub-skills
        updateExcludedSubSkills: state => {
            state.isUpdating = true;
        },
        excludedSubSkillsUpdated: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.excludedSubSkills =
                    payload.excludedSubSkills;
            }
            state.isUpdating = false;
        },
        excludedSubSkillsUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Unit numbers
        addUnitNumber: state => {
            state.isUpdating = true;
        },
        unitNumberAdded: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.unitNumber = payload.unitNumber;
            }
            state.isUpdating = false;
        },
        unitNumberAddFailed: state => {
            state.isUpdating = false;
        },
        deleteUnitNumber: state => {
            state.isUpdating = true;
        },
        unitNumberDeleted: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.unitNumber = payload.unitNumber;
            }
            state.isUpdating = false;
        },
        unitNumberDeleteFailed: state => {
            state.isUpdating = false;
        },

        // Homecare tasks
        addHomecareTask: state => {
            state.isUpdating = true;
        },
        homecareTaskAdded: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.tasks = payload.tasks;
            }
            state.isUpdating = false;
        },
        homecareTaskAddFailed: state => {
            state.isUpdating = false;
        },
        updateHomecareTask: state => {
            state.isUpdating = true;
        },
        homecareTaskUpdated: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.tasks = payload.tasks;
            }
            state.isUpdating = false;
        },
        homecareTaskUpdateFailed: state => {
            state.isUpdating = false;
        },
        deleteHomecareTask: state => {
            state.isUpdating = true;
        },
        homecareTaskDeleted: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.tasks = payload.tasks;
            }
            state.isUpdating = false;
        },
        homecareTaskDeleteFailed: state => {
            state.isUpdating = false;
        },

        // Homecare patients
        addHomecarePatient: state => {
            state.isUpdating = true;
        },
        homecarePatientAdded: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.patients = payload.patients;
            }
            state.isUpdating = false;
        },
        homecarePatientAddFailed: state => {
            state.isUpdating = false;
        },
        updateHomecarePatient: state => {
            state.isUpdating = true;
        },
        homecarePatientUpdated: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.patients = payload.patients;
            }
            state.isUpdating = false;
        },
        homecarePatientUpdateFailed: state => {
            state.isUpdating = false;
        },
        deleteHomecarePatient: state => {
            state.isUpdating = true;
        },
        homecarePatientDeleted: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.patients = payload.patients;
            }
            state.isUpdating = false;
        },
        homecarePatientDeleteFailed: state => {
            state.isUpdating = false;
        },

        // Bulletins
        updateBulletins: state => {
            state.isUpdating = true;
        },
        bulletinsUpdated: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.bulletins = payload.bulletins;
            }
            state.isUpdating = false;
        },
        bulletinsUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Schedule presets — use isUpdating, NOT isLoading, to avoid
        // unmounting tab content in CompanySettings which watches isLoading.
        fetchSchedulePresets: state => {
            state.isUpdating = true;
        },
        schedulePresetsFetched: (state, { payload }) => {
            state.schedulePresets = payload;
            state.isUpdating = false;
        },
        schedulePresetsFetchFailed: state => {
            state.isUpdating = false;
        },
        updateSchedulePresets: state => {
            state.isUpdating = true;
        },
        schedulePresetsUpdated: (state, { payload }) => {
            state.schedulePresets = payload;
            state.isUpdating = false;
        },
        schedulePresetsUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Unit persons (unit managers)
        updateUnitPersons: state => {
            state.isUpdating = true;
        },
        unitPersonsUpdated: (state, { payload }) => {
            if (state.singleCompany) {
                state.singleCompany.unitPersons = payload;
            }
            state.isUpdating = false;
        },
        unitPersonsUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Company logo
        setCompanyLogoUrl: (state, { payload }) => {
            state.companyLogoUrl = payload;
        },
        updateCompanyLogo: state => {
            state.isUpdating = true;
        },
        companyLogoUpdated: (state, { payload }) => {
            // Store the updated logo URL so shift cards stay in sync
            if (payload?.secure_url) {
                state.companyLogoUrl = payload.secure_url;
            } else if (typeof payload === 'string') {
                state.companyLogoUrl = payload;
            }
            state.isUpdating = false;
        },
        companyLogoUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Block list
        blockStaffer: state => {
            state.isUpdating = true;
        },
        stafferBlocked: (state, { payload }) => {
            if (state.singleCompany && state.singleCompany.creator) {
                state.singleCompany.creator.blocked_list = payload;
            }
            state.isUpdating = false;
        },
        stafferBlockFailed: state => {
            state.isUpdating = false;
        },
        unblockStaffer: state => {
            state.isUpdating = true;
        },
        stafferUnblocked: (state, { payload }) => {
            if (state.singleCompany && state.singleCompany.creator) {
                state.singleCompany.creator.blocked_list = payload;
            }
            state.isUpdating = false;
        },
        stafferUnblockFailed: state => {
            state.isUpdating = false;
        },

        // Secondary logins
        updateSecondaryLogins: state => {
            state.isUpdating = true;
        },
        secondaryLoginsUpdated: (state, { payload }) => {
            if (state.singleCompany && state.singleCompany.creator) {
                state.singleCompany.creator.secondaryLogins = payload;
            }
            state.isUpdating = false;
        },
        secondaryLoginsUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Email categories
        updateEmailCategories: state => {
            state.isUpdating = true;
        },
        emailCategoriesUpdated: (state, { payload }) => {
            if (state.singleCompany && state.singleCompany.creator) {
                state.singleCompany.creator.otherEmailsWithCategories = payload;
            }
            state.isUpdating = false;
        },
        emailCategoriesUpdateFailed: state => {
            state.isUpdating = false;
        },

        // Fire-and-forget actions (no state change, just trigger saga)
        changeCompanyPassword: state => {
            state.isUpdating = true;
        },
        companyPasswordChanged: state => {
            state.isUpdating = false;
        },
        companyPasswordChangeFailed: state => {
            state.isUpdating = false;
        },

        resetCompanyPassword: state => {
            state.isUpdating = true;
        },
        companyPasswordReset: state => {
            state.isUpdating = false;
        },
        companyPasswordResetFailed: state => {
            state.isUpdating = false;
        },

        sendCompanyNotification: state => {
            state.isUpdating = true;
        },
        companyNotificationSent: state => {
            state.isUpdating = false;
        },
        companyNotificationSendFailed: state => {
            state.isUpdating = false;
        },

        deleteCompany: state => {
            state.isUpdating = true;
        },
        companyDeleted: state => {
            state.isUpdating = false;
        },
        companyDeleteFailed: state => {
            state.isUpdating = false;
        },
    },
});

// selectors
const getSelf = state => state.companies;

export const getCompanies = createSelector(getSelf, state => state.data);

export const isLoading = createSelector(getSelf, state => state.isLoading);

export const getSingleCompany = createSelector(
    getSelf,
    state => state.singleCompany,
);

export const getCompanyId = createSelector(getSelf, state =>
    state.singleCompany ? state.singleCompany._id : null,
);

export const getIsUpdating = createSelector(getSelf, state => state.isUpdating);

export const getSchedulePresets = createSelector(
    getSelf,
    state => state.schedulePresets,
);

export const getCompanyLogoUrl = createSelector(
    getSelf,
    state => state.companyLogoUrl,
);

export const {
    fetchCompanies,
    companiesFetched,
    companiesFetchedFailed,
    fetchSingleCompany,
    singleCompanyFetched,
    singleCompanyFetchFailed,
    updateCompany,
    companyUpdated,
    companyUpdateFailed,
    updateSkillRates,
    skillRatesUpdated,
    skillRatesUpdateFailed,
    updateExcludedSubSkills,
    excludedSubSkillsUpdated,
    excludedSubSkillsUpdateFailed,
    addUnitNumber,
    unitNumberAdded,
    unitNumberAddFailed,
    deleteUnitNumber,
    unitNumberDeleted,
    unitNumberDeleteFailed,
    addHomecareTask,
    homecareTaskAdded,
    homecareTaskAddFailed,
    updateHomecareTask,
    homecareTaskUpdated,
    homecareTaskUpdateFailed,
    deleteHomecareTask,
    homecareTaskDeleted,
    homecareTaskDeleteFailed,
    addHomecarePatient,
    homecarePatientAdded,
    homecarePatientAddFailed,
    updateHomecarePatient,
    homecarePatientUpdated,
    homecarePatientUpdateFailed,
    deleteHomecarePatient,
    homecarePatientDeleted,
    homecarePatientDeleteFailed,
    updateBulletins,
    bulletinsUpdated,
    bulletinsUpdateFailed,
    // Schedule presets
    fetchSchedulePresets,
    schedulePresetsFetched,
    schedulePresetsFetchFailed,
    updateSchedulePresets,
    schedulePresetsUpdated,
    schedulePresetsUpdateFailed,
    // Unit persons
    updateUnitPersons,
    unitPersonsUpdated,
    unitPersonsUpdateFailed,
    // Company logo
    setCompanyLogoUrl,
    updateCompanyLogo,
    companyLogoUpdated,
    companyLogoUpdateFailed,
    // Contacts
    // Block list
    blockStaffer,
    stafferBlocked,
    stafferBlockFailed,
    unblockStaffer,
    stafferUnblocked,
    stafferUnblockFailed,
    // Secondary logins
    updateSecondaryLogins,
    secondaryLoginsUpdated,
    secondaryLoginsUpdateFailed,
    // Email categories
    updateEmailCategories,
    emailCategoriesUpdated,
    emailCategoriesUpdateFailed,
    // Password / notification / delete
    changeCompanyPassword,
    companyPasswordChanged,
    companyPasswordChangeFailed,
    resetCompanyPassword,
    companyPasswordReset,
    companyPasswordResetFailed,
    sendCompanyNotification,
    companyNotificationSent,
    companyNotificationSendFailed,
    deleteCompany,
    companyDeleted,
    companyDeleteFailed,
} = companySlice.actions;

export default companySlice.reducer;
